/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { PostHogProvider as PHProvider } from 'posthog-js/react'
import React, { useEffect, useState } from 'react'
import {
  COOKIE_ACCESS_TOKEN,
  getCookieAuthUser,
  getCookieProfileUser,
  getCookieValue
} from '@wiggot/auth'
import { decodeJWTCookieAccessToken } from '@wiggot/auth/utils/auth0.utils'
import { rolesByAuthUser } from '@wiggot/auth/utils/roles'
import { getMacrobolsaUser } from '../../../../apis-ssr/utils/macrobolsa/get-macrobolsa-user-status-client'
import { IProfileUser } from '../../../../interfaces/src/user.interface'
import { isBlacklisted } from './utils/is-blacklisted'
import type { RolesByAuthUser } from '@wiggot/auth/utils/roles'

type postHogType = typeof import('posthog-js').default

export function PostHogProvider({
  stage,
  children,
  posthogKey,
  posthogHost
}: {
  stage: string
  children: React.ReactNode
  posthogKey: string
  posthogHost: string
}) {
  const isProd = stage === 'prod'
  const [instance, setInstance] = useState<postHogType | null>(null)

  const canRunPosthog = () => {
    if (typeof window === 'undefined') {
      return false
    }

    if (!isProd) {
      return false
    }

    if (isBlacklisted(window.location.pathname)) {
      return false
    }

    return true
  }

  useEffect(() => {
    const init = async () => {
      if (!canRunPosthog()) {
        return
      }

      const authUser = getCookieAuthUser()
      const roles = authUser ? rolesByAuthUser(authUser) : null
      const profileUser: IProfileUser | null = getCookieProfileUser()
      const { hasMacrobolsaUser } = getMacrobolsaUser()
      const accessToken = getCookieValue(COOKIE_ACCESS_TOKEN)
      const decodeAccessToken = accessToken
        ? decodeJWTCookieAccessToken(accessToken)
        : null

      try {
        const posthog = await import('posthog-js').then((m) => m.default)
        setInstance(posthog)
        posthog.init(posthogKey!, {
          api_host: posthogHost,
          ui_host: 'https://us.posthog.com',
          defaults: '2025-05-24',
          autocapture: false,
          capture_exceptions: true // This enables capturing exceptions using Error Tracking,
        })

        posthog.identify(profileUser?.userId?.toString(), {
          phone: profileUser?.phoneNumber,
          email: profileUser?.email,
          name: profileUser?.fullname,
          signed_up: profileUser?.createdAt,
          plan: profileUser?.planType,
          rol: getUserRole(roles),
          macrobolsa: hasMacrobolsaUser,
          agency: profileUser?.agencyId,
          uuid: decodeAccessToken?.user.id
        })
      } catch (error) {
        console.error(error)
      }
    }
    init()
  }, [])

  if (!isProd || !instance) {
    return children
  }

  return <PHProvider client={instance}>{children}</PHProvider>
}

const getUserRole = (roles: Partial<RolesByAuthUser> | null) => {
  if (!roles) {
    return 'NO_IDENTIFIED'
  }

  if (roles.isAdminAgency) {
    return 'ADMIN_AGENCY'
  }

  if (roles.isIndependentAgent) {
    return 'INDEPENDENT_BROKER'
  }

  if (roles.isBrokerWithAgency) {
    return 'BROKER_AGENCY'
  }

  return 'NO_IDENTIFIED'
}
